import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Footer } from '@/components/Footer';

interface Section {
  id: string;
  title: string;
  icon: string;
}

export default function ReadmePage() {
  const [activeSection, setActiveSection] = useState('process-flow');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const sections: Section[] = [
    { id: 'process-flow', title: '業務流程', icon: '⚡' },
    { id: 'overview', title: '系統概述', icon: '🎯' },
    { id: 'architecture', title: '架構設計', icon: '🏗️' },
    { id: 'data-model', title: '資料模型', icon: '📊' },
    { id: 'features', title: '功能模組', icon: '✨' },
    { id: 'ui-guide', title: 'UI 設計', icon: '🎨' },
    { id: 'tech-stack', title: '技術架構', icon: '⚙️' },
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc] grid-pattern">
      {/* Header - Same as Dashboard */}
      <header className="bg-white border-b border-[#e2e8f0]">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="p-2 hover:bg-[#f8fafc] rounded-lg transition-colors"
                aria-label="返回首頁"
              >
                <svg className="w-5 h-5 text-[#64748b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </Link>
              <div>
                <h1 className="text-xl font-bold text-[#0f172a]">
                  設計說明文件
                </h1>
                <p className="text-sm text-[#64748b] mt-0.5">
                  製造排程系統 POC <span className="text-[#0066cc]/50">•</span> 設計架構與技術文件
                </p>
              </div>
            </div>
            <Link
              to="/"
              className="px-4 py-2 bg-gradient-to-r from-[#0066cc] to-[#0052a3] hover:from-[#0052a3] hover:to-[#004080] text-white rounded-lg transition-all text-sm font-semibold shadow-md hover:shadow-lg active:scale-95"
            >
              返回系統
            </Link>
          </div>
        </div>
      </header>

      {/* 主要內容 */}
      <main className="w-full px-4 py-8 sm:px-6 lg:px-8" role="main">
        <div className="max-w-[1800px] mx-auto">
          {/* 側邊導航與內容區域 */}
          <div className="flex gap-6 relative">
            {/* 側邊導航 */}
            <aside
              className={`flex-shrink-0 transition-all duration-300 ${
                isSidebarOpen ? 'w-64 opacity-100' : 'w-0 opacity-0 overflow-hidden'
              }`}
            >
              <nav className="sticky top-24 bg-white rounded-xl border-2 border-[#e2e8f0] p-4 shadow-sm">
                <h2 className="text-sm font-bold text-[#64748b] uppercase tracking-wider mb-4 px-2">
                  目錄導航
                </h2>
                <ul className="space-y-1">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <button
                        onClick={() => setActiveSection(section.id)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all text-left ${
                          activeSection === section.id
                            ? 'bg-gradient-to-r from-[#0066cc] to-[#0052a3] text-white shadow-md'
                            : 'text-[#475569] hover:bg-[#f1f5f9] hover:text-[#0f172a]'
                        }`}
                      >
                        <span className="text-lg">{section.icon}</span>
                        <span className="font-medium">{section.title}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </aside>

            {/* 側邊欄切換按鈕 */}
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className={`fixed left-0 top-1/2 -translate-y-1/2 z-50 bg-white border-2 border-[#e2e8f0] rounded-r-lg shadow-lg p-2 transition-all duration-300 hover:border-[#0066cc]/30 hover:shadow-xl ${
                isSidebarOpen ? 'translate-x-64' : 'translate-x-0'
              }`}
              aria-label={isSidebarOpen ? '隱藏目錄' : '顯示目錄'}
            >
              <svg
                className={`w-5 h-5 text-[#64748b] transition-transform duration-300 ${
                  isSidebarOpen ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* 主要內容區域 */}
            <div className="flex-1 min-w-0">
              <div className="bg-white rounded-xl border-2 border-[#e2e8f0] p-8 shadow-sm">
                {activeSection === 'process-flow' && <ProcessFlowSection />}
                {activeSection === 'overview' && <OverviewSection />}
                {activeSection === 'architecture' && <ArchitectureSection />}
                {activeSection === 'data-model' && <DataModelSection />}
                {activeSection === 'features' && <FeaturesSection />}
                {activeSection === 'ui-guide' && <UIGuideSection />}
                {activeSection === 'tech-stack' && <TechStackSection />}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

// ==================== 業務流程章節 ====================
function ProcessFlowSection() {
  return (
    <div className="space-y-6">
      {/* 標題與圖例 */}
      <div className="flex items-center justify-between pb-4 border-b-2 border-[#e2e8f0]">
        <h2 className="text-2xl font-bold text-[#0f172a]">
          🔄 業務流程對比
        </h2>
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 bg-gradient-to-br from-[#0066cc] to-[#0052a3] rounded"></span>
            <span className="text-[#475569]">標準訂單流程</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 bg-gradient-to-br from-[#dc2626] to-[#b91c1c] rounded"></span>
            <span className="text-[#475569]">插單流程</span>
          </div>
        </div>
      </div>

      {/* 並排對比流程圖 */}
      <div className="grid grid-cols-2 gap-6">
        {/* 左側：標準訂單流程 */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5">
          <h3 className="text-lg font-bold text-[#0f172a] mb-5 flex items-center gap-2">
            <span className="text-xl">🏃</span>
            標準訂單流程
          </h3>

          {/* 流程步驟 */}
          <div className="space-y-3">
            {[
              { num: 1, icon: '👤', title: '使用者建立訂單', type: 'User', color: 'blue', desc: ['點擊「➕ 新增訂單」', '填寫產品、客戶、數量', '系統建立 Order（待處理）'] },
              { num: 2, icon: '⚙️', title: '系統生成工單', type: 'Auto', color: 'amber', desc: ['生成主工單', '拆分子工單', '資源檢查（人機料）'] },
              { num: 3, icon: '🏭', title: '生產執行與報工', type: 'User+Sys', color: 'green', desc: ['作業員看到工單', '回報產出數量', '自動更新進度'] },
              { num: 4, icon: '✅', title: '訂單完成', type: 'System', color: 'slate', desc: ['進度 = 100%', '狀態更新為已完成', '資料歸檔'] },
            ].map((step, idx) => (
              <div key={step.num} className="flex items-center gap-3">
                <div className={`flex-shrink-0 w-10 h-10 bg-gradient-to-br from-${step.color}-500 to-${step.color}-600 rounded-full flex items-center justify-center shadow-md`}>
                  <span className="text-white font-bold">{step.num}</span>
                </div>
                <div className={`flex-1 bg-white border-2 border-${step.color}-300 rounded-lg p-4 shadow-sm`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-sm font-bold text-${step.color}-900`}>{step.icon} {step.title}</span>
                    <span className={`px-2 py-0.5 bg-${step.color}-100 text-${step.color}-700 text-xs font-semibold rounded`}>{step.type}</span>
                  </div>
                  <div className={`text-xs text-${step.color}-700 space-y-1`}>
                    {step.desc.map((d, i) => <p key={i}>• {d}</p>)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 右側：插單流程 */}
        <div className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 rounded-xl p-5">
          <h3 className="text-lg font-bold text-[#0f172a] mb-5 flex items-center gap-2">
            <span className="text-xl">⚡</span>
            插單流程
          </h3>

          {/* 流程步驟 */}
          <div className="space-y-3">
            {[
              { num: 1, icon: '👤', title: '使用者建立急單', type: 'User', color: 'red', desc: ['點擊「⚡ 插單」', '填寫壓縮交期', '可啟用交期壓縮模擬'] },
              { num: 2, icon: '🔍', title: '衝突檢測與模擬', type: 'Analysis', color: 'orange', desc: ['識別衝突資源', '時段衝突分析', '影響範圍模擬', '顯示：影響工單數、延後天數'] },
              { num: 3, icon: '🤔', title: '使用者審核決策', type: '⚠️ 介入', color: 'purple', desc: ['選項A：接受插單 → 工單延後', '選項B：拒絕插單 → 排程不變', '可調整：優先級、交期、替代資源'] },
              { num: 4, icon: '✅', title: '執行或取消', type: 'Result', color: 'green', desc: ['接受：插入排程 → 調整工單時間', '拒絕：急單取消 → 原排程不變'] },
            ].map((step, idx) => (
              <div key={step.num} className="flex items-center gap-3">
                <div className={`flex-shrink-0 w-10 h-10 bg-gradient-to-br from-${step.color}-500 to-${step.color}-600 rounded-full flex items-center justify-center shadow-md`}>
                  <span className="text-white font-bold">{step.num}</span>
                </div>
                <div className={`flex-1 bg-white border-2 border-${step.color}-300 rounded-lg p-4 shadow-sm`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-sm font-bold text-${step.color}-900`}>{step.icon} {step.title}</span>
                    <span className={`px-2 py-0.5 bg-${step.color}-100 text-${step.color}-700 text-xs font-semibold rounded`}>{step.type}</span>
                  </div>
                  <div className={`text-xs text-${step.color}-700 space-y-1`}>
                    {step.desc.map((d, i) => <p key={i}>• {d}</p>)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 對比總結表 */}
      <div className="bg-gradient-to-br from-slate-100 to-zinc-100 border-2 border-slate-300 rounded-xl p-5">
        <h3 className="text-lg font-bold text-slate-900 mb-4">📋 流程差異對比</h3>
        <div className="grid grid-cols-3 gap-4 text-center">
          {[
            { title: '標準流程特點', color: 'blue', items: ['自動執行，無需介入', '按標準工藝流程', '僅影響本訂單'] },
            { title: '流程差異', color: 'slate', items: ['觸發方式不同', '衝突檢測與否', '是否需使用者審核'] },
            { title: '插單流程特點', color: 'red', items: ['需使用者審核決策', '影響多個現有工單', '可手動調整優先'] },
          ].map((col) => (
            <div key={col.title} className="space-y-2">
              <div className={`font-bold text-${col.color}-900 bg-${col.color}-100 rounded-lg py-2`}>{col.title}</div>
              <div className="text-xs text-slate-700 bg-white rounded-lg p-3 text-left space-y-1">
                {col.items.map((item) => <p key={item}>• {item}</p>)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 設計理論基礎與最佳實踐 */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl p-6">
        <h3 className="text-xl font-bold text-[#0f172a] mb-6 flex items-center gap-2">
          <span className="text-2xl">📚</span>
          設計理論基礎與世界大廠最佳實踐
        </h3>

        {/* 理論基礎 */}
        <div className="bg-white border-2 border-indigo-300 rounded-xl p-5 mb-5">
          <h4 className="text-lg font-bold text-[#0f172a] mb-4 flex items-center gap-2">
            <span>🎓</span>
            理論基礎 (Theoretical Foundation)
          </h4>
          <div className="space-y-4">
            {/* TOC 理論 */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-l-4 border-blue-500 rounded-r-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <h5 className="font-bold text-[#0f172a]">限制理論 (Theory of Constraints, TOC)</h5>
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded">核心理論</span>
              </div>
              <p className="text-sm text-[#1e293b] mb-2">
                由 Eliyahu M. Goldratt 提出，核心思想是：<strong>任何系統至少存在一個限制因素，決定整體系統的產出</strong>。
                透過識別並聚焦於瓶頸資源，可最大化系統整體產能。
              </p>
              <div className="bg-white border border-blue-200 rounded-lg p-3 text-xs text-[#1e293b] space-y-1">
                <p><strong>Drum-Buffer-Rope (DBR) 方法：</strong></p>
                <ul className="ml-4 space-y-1">
                  <li>• <strong>Drum (鼓)</strong>：瓶頸資源設定生產節奏</li>
                  <li>• <strong>Buffer (緩衝)</strong>：在瓶頸前保持緩衝庫存，確保瓶頸不會停工</li>
                  <li>• <strong>Rope (繩)</strong>：控制物料釋放時機，避免在制品過多</li>
                </ul>
              </div>
              <div className="mt-2 text-xs text-blue-600">
                <a href="https://www.cyberplan.it/en/the-method-dbr-drum-buffer-rope-for-production-planning/" target="_blank" rel="noopener noreferrer" className="hover:underline">
                  📖 參考：CyberPlan DBR 說明
                </a>
                <span className="mx-2">•</span>
                <a href="https://www.6sigma.us/six-sigma-in-focus/drum-buffer-rope-dbr/" target="_blank" rel="noopener noreferrer" className="hover:underline">
                  6 Sigma: DBR 指南
                </a>
                <span className="mx-2">•</span>
                <a href="https://www.pmi.org/learning/library/drum-buffer-rope-critical-chain-buffering-8526" target="_blank" rel="noopener noreferrer" className="hover:underline">
                  PMI: DBR 方法論
                </a>
              </div>
            </div>

            {/* 本體論方法 */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-l-4 border-purple-500 rounded-r-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <h5 className="font-bold text-[#0f172a]">製造本體論 (Manufacturing Ontology)</h5>
                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded">知識表示</span>
              </div>
              <p className="text-sm text-[#1e293b] mb-2">
                <strong>本體論 (Ontology)</strong> 是對特定領域概念及其關係的形式化描述。在製造業中，本體論用於：
              </p>
              <ul className="text-sm text-[#1e293b] space-y-1 ml-4 mb-3">
                <li>• <strong>語意互操作性</strong>：不同系統間的資料理解與交換</li>
                <li>• <strong>知識圖譜</strong>：建立製造資源的語意關聯網絡</li>
                <li>• <strong>智慧排程</strong>：基於本體論的規則推理與資源匹配</li>
              </ul>
              <div className="bg-white border border-purple-200 rounded-lg p-3 text-xs text-[#1e293b]">
                <p className="font-semibold mb-2">本系統的三層工單本體論結構：</p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-blue-50 rounded p-2">
                    <div className="font-bold text-[#0f172a]">Order</div>
                    <div className="text-purple-600">訂單實體</div>
                  </div>
                  <div className="bg-amber-50 rounded p-2">
                    <div className="font-bold text-[#0f172a]">MainWorkOrder</div>
                    <div className="text-purple-600">工藝綁定</div>
                  </div>
                  <div className="bg-green-50 rounded p-2">
                    <div className="font-bold text-[#0f172a]">SubWorkOrder</div>
                    <div className="text-purple-600">資源執行</div>
                  </div>
                </div>
              </div>
              <div className="mt-2 text-xs text-purple-600">
                <a href="https://link.springer.com/article/10.1007/s10845-024-02425-z" target="_blank" rel="noopener noreferrer" className="hover:underline">
                  📖 參考：Springer - 製造本體論分類研究 (2024)
                </a>
              </div>
            </div>

            {/* 方法論 */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 rounded-r-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <h5 className="font-bold text-[#0f172a]">精益生產與 JIT (Lean Manufacturing & Just-In-Time)</h5>
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded">方法論</span>
              </div>
              <p className="text-sm text-[#1e293b] mb-2">
                <strong>Toyota Production System (TPS)</strong> 強調消除浪費、持續改善、尊重人員。
                <strong>Just-In-Time (JIT)</strong> 追求在需要的時候生產需要的數量。
              </p>
              <div className="bg-white border border-amber-200 rounded-lg p-3 text-xs text-[#1e293b]">
                <p className="font-semibold mb-1">本系統應用的 JIT 原則：</p>
                <ul className="ml-4 space-y-1">
                  <li>• <strong>資源自動檢查</strong>：人機料充足才開啟工單（避免浪費）</li>
                  <li>• <strong>可視化管理</strong>：甘特圖即時顯示排程狀態</li>
                  <li>• <strong>拉式生產</strong>：基於訂單需求驅動生產（非推式）</li>
                </ul>
              </div>
              <div className="mt-2 text-xs text-amber-600">
                <a href="https://global.toyota/en/company/vision-and-philosophy/production-system/index.html" target="_blank" rel="noopener noreferrer" className="hover:underline">
                  📖 參考：Toyota Production System 官方說明
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* 世界大廠最佳實踐 */}
        <div className="bg-white border-2 border-indigo-300 rounded-xl p-5 mb-5">
          <h4 className="text-lg font-bold text-[#0f172a] mb-4 flex items-center gap-2">
            <span>🏢</span>
            世界大廠最佳實踐 (Industry Best Practices)
          </h4>
          <div className="grid grid-cols-3 gap-4">
            {/* Toyota */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
              <div className="text-center mb-3">
                <div className="text-3xl mb-2">🚗</div>
                <h5 className="font-bold text-[#0f172a]">Toyota</h5>
                <p className="text-xs text-blue-600">Toyota Production System</p>
              </div>
              <div className="text-xs text-[#1e293b] space-y-2">
                <p><strong>精益生產方法：</strong></p>
                <ul className="ml-3 space-y-1">
                  <li>• Heijunka (生產平準化)</li>
                  <li>• Kanban (看板系統)</li>
                  <li>• Andon (異常顯示)</li>
                  <li>• Kaizen (持續改善)</li>
                </ul>
                <p className="text-blue-600 italic mt-2">
                  "在需要的時候、生產需要的數量"
                </p>
              </div>
            </div>

            {/* SAP */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
              <div className="text-center mb-3">
                <div className="text-3xl mb-2">📊</div>
                <h5 className="font-bold text-[#0f172a]">SAP</h5>
                <p className="text-xs text-amber-600">SAP PP & PP/DS</p>
              </div>
              <div className="text-xs text-[#1e293b] space-y-2">
                <p><strong>排程優化方法：</strong></p>
                <ul className="ml-3 space-y-1">
                  <li>• 訂單優先級分類</li>
                  <li>• 產能規劃與需求平衡</li>
                  <li>• 衝突工單自動調整</li>
                  <li>• 急單波道處理</li>
                </ul>
                <p className="text-amber-600 italic mt-2">
                  "優先級驅動的排程邏輯"
                </p>
              </div>
            </div>

            {/* Siemens */}
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-200 rounded-xl p-4">
              <div className="text-center mb-3">
                <div className="text-3xl mb-2">⚙️</div>
                <h5 className="font-bold text-[#0f172a]">Siemens</h5>
                <p className="text-xs text-teal-600">Opcenter APS</p>
              </div>
              <div className="text-xs text-[#1e293b] space-y-2">
                <p><strong>進階排程功能：</strong></p>
                <ul className="ml-3 space-y-1">
                  <li>• 有限產能排程</li>
                  <li>• 動態排程調整</li>
                  <li>• 資源最佳化分配</li>
                  <li>• 即時監控與回饋</li>
                </ul>
                <p className="text-teal-600 italic mt-2">
                  "基於限制的排程最佳化"
                </p>
              </div>
            </div>
          </div>
          <div className="mt-3 text-xs text-slate-500 text-center">
            參考來源：
            <a href="https://asprova.net/wp-content/uploads/2021/02/TOYOTA-PRODUCTION-SYSTEM.pdf" target="_blank" rel="noopener noreferrer" className="hover:underline mx-1">Toyota TPS 說明文件</a>
            •
            <a href="https://www.sap.com/assetdetail/2023/04/3cba5c1d-707e-0010-bca6-c68f7e60039b.html" target="_blank" rel="noopener noreferrer" className="hover:underline mx-1">SAP PP/DS 說明</a>
            •
            <a href="https://plm.sw.siemens.com/en-US/opcenter/advanced-planning-scheduling-aps/" target="_blank" rel="noopener noreferrer" className="hover:underline mx-1">Siemens Opcenter APS</a>
          </div>
        </div>

        {/* 為什麼是這樣的流程設計 */}
        <div className="bg-white border-2 border-indigo-300 rounded-xl p-5 mb-5">
          <h4 className="text-lg font-bold text-[#0f172a] mb-4 flex items-center gap-2">
            <span>💡</span>
            為什麼是這樣的流程設計？
          </h4>
          <div className="space-y-4">
            {/* 標準流程設計理由 */}
            <div className="bg-gradient-to-r from-emerald-50 to-green-50 border-l-4 border-emerald-500 rounded-r-lg p-4">
              <h5 className="font-bold text-[#0f172a] mb-2">標準流程設計理由</h5>
              <div className="text-sm text-[#1e293b] space-y-3">
                <div>
                  <p className="font-semibold text-emerald-900">1. 自動化執行 (Automation)</p>
                  <p className="text-emerald-700 ml-4">
                    標準訂單無需人工干預，系統自動生成工單、檢查資源、分配排程。
                    這符合 <strong>JIT 原則</strong>：減少浪費、提升效率。
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-emerald-900">2. 標準化路徑 (Standardization)</p>
                  <p className="text-emerald-700 ml-4">
                    按照預定工藝流程執行，確保產品品質一致性。
                    這符合 <strong>Toyota 的標準化作業</strong> 原則。
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-emerald-900">3. 本地化影響 (Localization)</p>
                  <p className="text-emerald-700 ml-4">
                    標準訂單僅影響自身，不會干擾其他工單。
                    這符合 <strong>TOC 的非瓶頸資源利用</strong> 原則：非瓶頸應配合瓶頸，而非獨立優化。
                  </p>
                </div>
              </div>
            </div>

            {/* 插單流程設計理由 */}
            <div className="bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-500 rounded-r-lg p-4">
              <h5 className="font-bold text-[#0f172a] mb-2">插單流程設計理由</h5>
              <div className="text-sm text-[#1e293b] space-y-3">
                <div>
                  <p className="font-semibold text-red-900">1. 使用者審核決策 (Human Decision)</p>
                  <p className="text-red-700 ml-4">
                    插單會影響多個現有工單，必須由管理者評估後決定。
                    這符合 <strong>SAP 的優先級管理</strong> 機制：急單需要人工審核其影響範圍。
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-red-900">2. 衝突檢測與模擬 (Conflict Detection & Simulation)</p>
                  <p className="text-red-700 ml-4">
                    在插入前識別資源衝突、模擬影響範圍。
                    這符合 <strong>Siemens APS 的有限產能排程</strong> 方法：預先模擬可避免排程崩潰。
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-red-900">3. 靈活調整機制 (Flexibility)</p>
                  <p className="text-red-700 ml-4">
                    允許調整優先級、交期、替代資源。
                    這符合 <strong>Toyota 的 Jidoka (自働化)</strong> 原則：賦予人員決策權，可在異常時靈活應變。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 核心設計原則總結 */}
        <div className="bg-gradient-to-r from-slate-100 to-zinc-100 border-2 border-slate-300 rounded-xl p-5">
          <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <span>🎯</span>
            本系統的核心設計原則總結
          </h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">📦</span>
                <h5 className="font-bold text-slate-900">物件導向追蹤</h5>
              </div>
              <p className="text-slate-700 text-xs">
                以「物件為單位」的三層工單結構，精確追蹤每個生產物件的狀態。
                符合 <strong>製造本體論</strong> 的形式化描述方法。
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">⚠️</span>
                <h5 className="font-bold text-slate-900">瓶頸聚焦管理</h5>
              </div>
              <p className="text-slate-700 text-xs">
                插單時識別衝突資源（瓶頸），模擬其影響範圍。
                符合 <strong>TOC 的 DBR 方法</strong>：聚焦於瓶頸資源的管理。
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">👁️</span>
                <h5 className="font-bold text-slate-900">可視化透明化</h5>
              </div>
              <p className="text-slate-700 text-xs">
                甘特圖即時顯示排程狀態、衝突、延遲。
                符合 <strong>Toyota 的可視化管理</strong> 原則。
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">🔄</span>
                <h5 className="font-bold text-slate-900">人機協同決策</h5>
              </div>
              <p className="text-slate-700 text-xs">
                系統提供模擬資訊，由使用者做最終決策。
                符合 <strong>Industry 4.0 的人機協作</strong> 理念。
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// 系統概述
function OverviewSection() {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#0f172a] pb-4 border-b-2 border-[#e2e8f0]">
        🎯 系統概述
      </h2>

      <div className="space-y-6">
        {/* 專案目標 */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6">
          <h3 className="text-lg font-bold text-[#0f172a] mb-3">專案目標</h3>
          <p className="text-[#1e293b] leading-relaxed">
            本系統是一個<strong>製造業訂單與流程管理系統的 POC 專案</strong>，目標是建立一個以「物件為單位」追蹤製造生命週期的管理系統。
            系統採用三層工單結構（訂單 → 主工單 → 子工單），實現精細化的生產排程與資源管理。
          </p>
        </div>

        {/* 特色卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { icon: '🎨', title: '現代化 UI', desc: '採用 Tailwind CSS 打造的響應式介面', color: 'pink' },
            { icon: '⚡', title: '即時更新', desc: '使用 Zustand 狀態管理，實現即時資料同步', color: 'cyan' },
            { icon: '📊', title: '可視化排程', desc: '整合 DHTMLX Gantt，提供直觀的甘特圖視圖', color: 'amber' },
            { icon: '🔧', title: '模組化設計', desc: 'React + TypeScript 架構，確保可維護性', color: 'emerald' },
          ].map((feature) => (
            <div key={feature.title} className={`bg-gradient-to-br from-${feature.color}-50 to-${feature.color}-100 border border-${feature.color}-200 rounded-xl p-5`}>
              <h4 className={`font-bold text-${feature.color}-900 mb-2 flex items-center gap-2`}>
                <span className="text-xl">{feature.icon}</span>
                {feature.title}
              </h4>
              <p className={`text-sm text-${feature.color}-700`}>{feature.desc}</p>
            </div>
          ))}
        </div>

        {/* 系統特色 */}
        <div className="bg-white border-2 border-[#e2e8f0] rounded-xl p-6">
          <h4 className="font-bold text-[#0f172a] mb-4">💡 系統特色</h4>
          <ul className="space-y-3">
            {[
              { title: '三層工單結構', desc: '訂單 → 主工單 → 子工單的層級設計，精確追蹤生產進度' },
              { title: '多維度排程視圖', desc: '工單甘特圖、人員排程、資源甘特圖三種視圖滿足不同需求' },
              { title: '即時插單模擬', desc: '急單插入時自動識別衝突並模擬影響範圍' },
              { title: '資源自動檢查', desc: '子工單開啟前自動檢查人員、機台、物料是否充足' },
            ].map((item) => (
              <li key={item.title} className="flex items-start gap-3">
                <span className="text-emerald-500 text-xl">✓</span>
                <div>
                  <strong className="text-[#0f172a]">{item.title}</strong>
                  <p className="text-sm text-[#64748b] mt-1">{item.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// 架構設計
function ArchitectureSection() {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#0f172a] pb-4 border-b-2 border-[#e2e8f0]">
        🏗️ 架構設計
      </h2>

      <div className="space-y-6">
        {/* 三層架構 */}
        <div className="bg-gradient-to-br from-slate-50 to-zinc-50 border border-[#e2e8f0] rounded-xl p-6">
          <h3 className="text-lg font-bold text-[#0f172a] mb-4">三層架構</h3>
          <div className="space-y-4">
            {[
              { num: 1, title: '展示層', subtitle: 'Presentation Layer', desc: '甘特圖視圖、看板視圖、清單視圖、儀表板等使用者介面元件' },
              { num: 2, title: '業務邏輯層', subtitle: 'Business Logic Layer', desc: '訂單管理、工藝流程、工單管理、資源管理、排程引擎、插單處理' },
              { num: 3, title: '資料存取層', subtitle: 'Data Access Layer', desc: 'SQLite + Drizzle ORM 提供本地資料儲存與查詢功能' },
            ].map((layer) => (
              <div key={layer.num} className="bg-white border border-[#e2e8f0] rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#0066cc] to-[#0052a3] rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">{layer.num}</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-[#0f172a]">{layer.title}</h4>
                    <p className="text-xs text-[#64748b]">{layer.subtitle}</p>
                  </div>
                </div>
                <p className="text-sm text-[#64748b] ml-13">{layer.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 狀態管理架構 */}
        <div className="bg-white border-2 border-[#e2e8f0] rounded-xl p-6">
          <h3 className="text-lg font-bold text-[#0f172a] mb-4">狀態管理架構</h3>
          <div className="grid grid-cols-2 gap-4">
            {[
              { name: 'orderStore', icon: '📦', desc: '訂單、主工單、子工單狀態管理', color: 'blue' },
              { name: 'resourceStore', icon: '👥', desc: '人員、機台、物料資源狀態', color: 'green' },
              { name: 'reportStore', icon: '📝', desc: '報工資料與生產記錄', color: 'purple' },
              { name: 'scheduleStore', icon: '⏰', desc: '班表與排程相關狀態', color: 'amber' },
            ].map((store) => (
              <div key={store.name} className={`bg-${store.color}-50 border border-${store.color}-200 rounded-lg p-4`}>
                <h4 className={`font-semibold text-${store.color}-900 mb-2 flex items-center gap-2`}>
                  <span className="text-lg">{store.icon}</span>
                  {store.name}
                </h4>
                <p className={`text-sm text-${store.color}-700`}>{store.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// 資料模型
function DataModelSection() {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#0f172a] pb-4 border-b-2 border-[#e2e8f0]">
        📊 資料模型
      </h2>

      <div className="space-y-6">
        {/* 三層工單結構 */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
          <h3 className="text-lg font-bold text-[#0f172a] mb-4">三層工單結構</h3>
          <div className="bg-white border border-blue-200 rounded-lg p-4 text-center">
            <div className="mb-3">
              <span className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-lg font-bold">訂單 (Order)</span>
            </div>
            <div className="my-3">
              <span className="text-blue-500 text-2xl">↓</span>
            </div>
            <div className="my-3">
              <span className="inline-block bg-amber-100 text-amber-800 px-4 py-2 rounded-lg font-bold">主工單 (MainWorkOrder)</span>
            </div>
            <div className="mt-3">
              <span className="text-amber-500 text-2xl">↓</span>
            </div>
            <div className="mt-3">
              <span className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-lg font-bold">子工單 (SubWorkOrder)</span>
            </div>
          </div>
        </div>

        {/* 關鍵實體說明 */}
        <div className="bg-white border-2 border-[#e2e8f0] rounded-xl p-6">
          <h3 className="text-lg font-bold text-[#0f172a] mb-4">關鍵實體說明</h3>
          <div className="space-y-4">
            {[
              { title: 'Order (訂單)', desc: '包含產品、數量、交期、客戶等基本資訊，是整個生產流程的起點', color: 'blue' },
              { title: 'MainWorkOrder (主工單)', desc: '訂單與工藝流程綁定後生成，包含預期開始/結束時間、工藝路徑', color: 'amber' },
              { title: 'SubWorkOrder (子工單)', desc: '派發到具體站點、人員、機台，包含實際產出回報與資源綁定', color: 'green' },
              { title: 'Person/Machine/Material (資源)', desc: '人員、機台、物料資源，子工單開啟前需確保三者都充足', color: 'purple' },
              { title: 'OutputRecord (產出回報)', desc: '作業員回報的實際產出數量，與子工單關聯並影響進度計算', color: 'red' },
            ].map((entity) => (
              <div key={entity.title} className={`border-l-4 border-${entity.color}-500 pl-4 py-2`}>
                <h4 className={`font-bold text-${entity.color}-900`}>{entity.title}</h4>
                <p className="text-sm text-[#64748b] mt-1">{entity.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 重要業務規則 */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6">
          <h3 className="text-lg font-bold text-[#0f172a] mb-4">⚠️ 重要業務規則</h3>
          <ul className="space-y-3">
            {[
              { title: '子工單開啟條件', desc: '人機料三者都充足才能自動開啟' },
              { title: '進度計算', desc: '訂單進度 = 主工單進度加權平均；主工單進度 = 子工單進度加權平均' },
              { title: '延遲波及', desc: '任何子工單延遲會自動推算並影響所有依賴的後續工單' },
            ].map((rule) => (
              <li key={rule.title} className="flex items-start gap-2">
                <span className="text-amber-500">▸</span>
                <span className="text-sm text-[#0f172a]">
                  <strong>{rule.title}：</strong>
                  <span className="text-[#1e293b]">{rule.desc}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// 功能模組
function FeaturesSection() {
  const features = [
    {
      category: '核心功能',
      icon: '🎯',
      items: [
        { name: '訂單管理', desc: '建立、編輯、刪除訂單，追蹤訂單狀態與進度' },
        { name: '工單管理', desc: '三層工單結構管理，自動生成工單與排程' },
        { name: '排程引擎', desc: '資源檢查、延遲推算、插單模擬' },
      ]
    },
    {
      category: '排程視圖',
      icon: '📊',
      items: [
        { name: '工單甘特圖', desc: '三層樹狀結構甘特圖，支援拖曳調整' },
        { name: '人員排程', desc: '24小時人員排程視圖，班表管理' },
        { name: '資源甘特圖', desc: '機台、物料資源使用情況視覺化' },
      ]
    },
    {
      category: '生產管理',
      icon: '🏭',
      items: [
        { name: '生產報工', desc: '作業員回報產出，自動更新進度' },
        { name: '品質管理', desc: '檢驗記錄、不良品統計、品質分析' },
        { name: '瓶頸分析', desc: '識別生產瓶頸，提供改善建議' },
      ]
    },
    {
      category: '進階功能',
      icon: '⚡',
      items: [
        { name: '插單管理', desc: '急單插入模擬，衝突檢測與影響分析' },
        { name: '物件追蹤', desc: '多物件並行追蹤，實時監控生產狀態' },
        { name: '人員管理', desc: '人員資料、技能、歷史記錄管理' },
      ]
    },
  ];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#0f172a] pb-4 border-b-2 border-[#e2e8f0]">
        ✨ 功能模組
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {features.map((featureGroup) => (
          <div key={featureGroup.category} className="bg-white border-2 border-[#e2e8f0] rounded-xl overflow-hidden">
            <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4 border-b border-[#e2e8f0]">
              <h3 className="text-lg font-bold text-[#0f172a] flex items-center gap-2">
                <span>{featureGroup.icon}</span>
                {featureGroup.category}
              </h3>
            </div>
            <div className="p-6">
              <ul className="space-y-4">
                {featureGroup.items.map((item) => (
                  <li key={item.name} className="flex items-start gap-3">
                    <span className="text-[#0066cc] font-bold text-xl">›</span>
                    <div>
                      <h4 className="font-semibold text-[#0f172a]">{item.name}</h4>
                      <p className="text-sm text-[#64748b] mt-1">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* POC 優先級 */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
        <h3 className="text-lg font-bold text-green-900 mb-3">🎯 POC 優先級</h3>
        <div className="space-y-3">
          {[
            { priority: 'P0', color: 'red', items: '三層工單結構、工單甘特圖、人員列表' },
            { priority: 'P1', color: 'orange', items: '報工系統、人員甘特圖(24h)、班表管理' },
            { priority: 'P2', color: 'amber', items: '品質管理、資源甘特圖、插單模擬、多物件並行' },
          ].map((level) => (
            <div key={level.priority} className="bg-white border border-[#e2e8f0] rounded-lg p-4">
              <span className={`inline-block px-3 py-1 bg-${level.color}-500 text-white text-xs font-bold rounded mr-2`}>
                {level.priority}
              </span>
              <span className="text-[#475569]">{level.items}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// UI 設計指南
function UIGuideSection() {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#0f172a] pb-4 border-b-2 border-[#e2e8f0]">
        🎨 UI 設計指南
      </h2>

      <div className="space-y-6">
        {/* 色彩系統 */}
        <div className="bg-white border-2 border-[#e2e8f0] rounded-xl p-6">
          <h3 className="text-lg font-bold text-[#0f172a] mb-4">色彩系統</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { name: '主色調', color: 'bg-[#0066cc]', hex: '#0066cc' },
              { name: '深色調', color: 'bg-[#0f172a]', hex: '#0f172a' },
              { name: '次要文字', color: 'bg-[#64748b]', hex: '#64748b' },
              { name: '背景色', color: 'bg-[#f8fafc]', hex: '#f8fafc' },
            ].map((color) => (
              <div key={color.name}>
                <div className={`h-20 ${color.color} rounded-lg mb-2 shadow-sm border border-[#e2e8f0]`}></div>
                <p className="text-sm font-semibold text-[#0f172a]">{color.name}</p>
                <p className="text-xs text-[#64748b]">{color.hex}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 狀態顏色 */}
        <div className="bg-white border-2 border-[#e2e8f0] rounded-xl p-6">
          <h3 className="text-lg font-bold text-[#0f172a] mb-4">狀態顏色</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { name: '已完成', color: 'bg-green-100 text-green-700 border-green-200', icon: '✓' },
              { name: '進行中', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: '◐' },
              { name: '已延遲', color: 'bg-red-100 text-red-700 border-red-200', icon: '⚠' },
              { name: '待處理', color: 'bg-slate-100 text-slate-700 border-slate-200', icon: '○' },
            ].map((status) => (
              <div key={status.name} className="text-center">
                <div className={`inline-block w-full px-4 py-2 ${status.color} rounded-lg font-semibold text-sm mb-2`}>
                  {status.icon} {status.name}
                </div>
                <p className="text-xs text-[#64748b]">狀態指示器</p>
              </div>
            ))}
          </div>
        </div>

        {/* 交互設計原則 */}
        <div className="bg-white border-2 border-[#e2e8f0] rounded-xl p-6">
          <h3 className="text-lg font-bold text-[#0f172a] mb-4">🎭 交互設計原則</h3>
          <ul className="space-y-3">
            {[
              { title: '視覺反饋', desc: '所有交互元素都有明確的視覺反饋（hover、active、disabled）' },
              { title: '動畫效果', desc: '使用平滑的動畫過渡，提升使用者體驗' },
              { title: '無障礙設計', desc: '符合 WCAG 2.1 AA 標準，支援鍵盤導航與螢幕閱讀器' },
              { title: '空狀態設計', desc: '提供清晰的空狀態指引，告訴使用者下一步該做什麼' },
            ].map((principle) => (
              <li key={principle.title} className="flex items-start gap-3">
                <span className="text-[#0066cc] font-bold">✓</span>
                <div>
                  <strong className="text-[#0f172a]">{principle.title}</strong>
                  <p className="text-sm text-[#64748b] mt-1">{principle.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// 技術架構
function TechStackSection() {
  const techCategories = [
    { name: '前端框架', icon: '⚛️', items: ['React 18', 'TypeScript', 'Vite'] },
    { name: '狀態管理', icon: '📦', items: ['Zustand'] },
    { name: '樣式設計', icon: '🎨', items: ['Tailwind CSS', 'CSS Modules'] },
    { name: '圖表元件', icon: '📊', items: ['DHTMLX Gantt'] },
    { name: '資料庫', icon: '🗄️', items: ['SQLite', 'Drizzle ORM'] },
    { name: '建置工具', icon: '🔧', items: ['Bun', 'Vite'] },
  ];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#0f172a] pb-4 border-b-2 border-[#e2e8f0]">
        ⚙️ 技術架構
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {techCategories.map((category) => (
          <div key={category.name} className="bg-white border-2 border-[#e2e8f0] rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">{category.icon}</span>
              <h3 className="text-lg font-bold text-[#0f172a]">{category.name}</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {category.items.map((item) => (
                <span
                  key={item}
                  className="px-3 py-1.5 bg-gradient-to-r from-slate-50 to-slate-100 border border-[#e2e8f0] text-[#475569] rounded-lg text-sm font-medium"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* 快速開始 */}
      <div className="bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-200 rounded-xl p-6">
        <h3 className="text-lg font-bold text-[#0f172a] mb-4">🚀 快速開始</h3>
        <div className="bg-white border border-purple-200 rounded-lg p-4 font-mono text-sm space-y-2">
          <div>
            <div className="text-[#64748b]"># 初始化專案</div>
            <div className="text-green-700">bun init</div>
          </div>
          <div>
            <div className="text-[#64748b] mt-4"># 安裝依賴</div>
            <div className="text-green-700">bun install</div>
          </div>
          <div>
            <div className="text-[#64748b] mt-4"># 啟動開發伺服器</div>
            <div className="text-green-700">bun run dev</div>
          </div>
        </div>
      </div>

      {/* 專案結構 */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
        <h3 className="text-lg font-bold text-[#0f172a] mb-4">📂 專案結構</h3>
        <div className="bg-white border border-green-200 rounded-lg p-4 font-mono text-sm text-[#475569]">
          <pre>{`src/
├── components/        # React 元件
│   ├── Dashboard/     # 儀表板相關
│   ├── Forms/         # 表單元件
│   ├── GanttChart/    # 甘特圖元件
│   ├── Persons/       # 人員管理
│   ├── UI/            # 通用 UI 元件
│   └── ...
├── pages/             # 頁面組件
├── store/             # Zustand 狀態管理
├── types/             # TypeScript 型別定義
├── services/          # 資料服務
├── data/              # 靜態資料
└── styles/            # 全域樣式`}</pre>
        </div>
      </div>
    </div>
  );
}
